import { NgTemplateOutlet } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { MODULE_ROUTE_PREFIX } from '../../core/modules/module-routes';
import { ModuleCardModel } from './module-card.model';

/**
 * One module row on the dashboard: the icon in a tinted well, the name with
 * its version chip, the description, and the licensing verdict on the right —
 * the design system's module list card.
 *
 * An openable module is a real `<a>` around the whole row: it lands in the tab
 * order, opens in a new tab with the usual modifiers, and shows its target in
 * the status bar — none of which a click handler on a div gives. A module that
 * cannot be opened is deliberately not a link, so keyboard users are not sent
 * to something that will only apologise; the reason is on the row instead.
 *
 * Licensing shows as a pill: a green "Licensed" one for a held licence, an
 * amber "No licence" one — with a dashed border on the row — where the company
 * holds none. Modules with no licence relationship (outside the UMC catalog)
 * carry neither — an indicator on everything would say nothing.
 */
@Component({
  selector: 'app-module-card',
  imports: [RouterLink, NgTemplateOutlet, TranslatePipe],
  template: `
    @let card = module();

    <ng-template #tile>
      <span class="module-card__icon-well" aria-hidden="true">
        <i [class]="card.icon"></i>
      </span>

      <span class="module-card__body">
        <span class="module-card__title-row">
          <h2 class="module-card__title">{{ card.titleKey | translate }}</h2>
          @if (card.version; as version) {
            <span class="module-card__chip module-card__chip--version">v{{ version }}</span>
          }
          @if (card.category; as category) {
            <span class="module-card__chip">{{ category }}</span>
          }
        </span>

        @if (card.descriptionKey; as description) {
          <span class="module-card__description">{{ description | translate }}</span>
        }

        @if (card.noteKey; as note) {
          <span class="module-card__note">{{ note | translate }}</span>
        }
      </span>

      <span class="module-card__side">
        @if (card.license; as license) {
          <span
            class="module-card__license module-card__license--{{ license }}"
            [title]="'module.license.' + license + '.hint' | translate"
          >
            <i
              class="pi"
              [class.pi-verified]="license === 'licensed'"
              [class.pi-lock]="license === 'unlicensed'"
              [class.pi-user-minus]="license === 'noSeat'"
              aria-hidden="true"
            ></i>
            {{ 'module.license.' + license + '.label' | translate }}
          </span>
          <!-- Opens the licence dialog. A real button inside the tile link:
               its click must neither follow the link nor bubble into it. -->
          <button
            type="button"
            class="module-card__info"
            [attr.aria-label]="'module.license.info.open' | translate"
            [title]="'module.license.info.open' | translate"
            (click)="onInfo($event)"
          >
            <i class="pi pi-info-circle" aria-hidden="true"></i>
          </button>
        }
        @if (card.available) {
          <i class="module-card__arrow pi pi-arrow-right" aria-hidden="true"></i>
        }
      </span>
    </ng-template>

    @if (card.available) {
      <a
        class="module-card module-card--link"
        [class.module-card--licensed]="card.license === 'licensed'"
        [routerLink]="['/', prefix, card.id]"
      >
        <ng-container [ngTemplateOutlet]="tile" />
      </a>
    } @else {
      <div
        class="module-card module-card--unavailable"
        [class.module-card--unlicensed]="card.license === 'unlicensed' || card.license === 'noSeat'"
      >
        <ng-container [ngTemplateOutlet]="tile" />
      </div>
    }
  `,
  styleUrl: './module-card.scss',
})
export class ModuleCard {
  readonly module = input.required<ModuleCardModel>();

  /** The info button was pressed — the dashboard opens the licence dialog. */
  readonly licenseInfo = output<void>();

  protected readonly prefix = MODULE_ROUTE_PREFIX;

  protected onInfo(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.licenseInfo.emit();
  }
}
